import { yupResolver } from '@hookform/resolvers/yup';
import { FormEventHandler, useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePostSectionsRecspec } from '../../../../api/sections/sections';
import { useDefaultOptions } from '../../../../hooks/api';
import yup from '../../../../utils/custom-yup';
import FileDrop from '../../../atoms/FileDrop';
import ErrorModal from '../../../modules/ErrorModal';
import Modal from '../../../modules/Modal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface Props {
  sectionId: number;
  active: boolean;
  onClose: () => void;
}

/**
 * ReqSpec連携のインポートダイアログ
 * @param param0
 * @returns
 */
function ReqSpecImportModal({ sectionId, active, onClose }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const schema = yup.object({
    comment: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
    importFiles: yup.array().required(),
  });

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  // キャンセル
  const onCancelClicked = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  // #region RecSpec
  const options = useDefaultOptions(language);
  // RecSpecApi定義
  const { mutateAsync: recAsync, isLoading: recIsLoading, error: recError } = usePostSectionsRecspec(options);
  const [displayImportError, setDisplayImportError] = useState(false); // エラー
  const [resultMessage, setResultMessage] = useState('');
  const [displayResult, setDisplayResult] = useState(false);

  useEffect(() => {
    setDisplayImportError(recError != null);
  }, [recError]);

  // インポート実行
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      void handleSubmit(async (data: FieldValues) => {
        if (data.importFiles.length > 0) {
          await recAsync(
            { sectionId, data: { file: data.importFiles?.[0] } },
            {
              // API アクセスが成功したら、ダウンロードを実行
              onSuccess: (response) => {
                // ハイライトを消す
                document.querySelectorAll('[reqspecdisp]').forEach((element: Element) => {
                  element.removeAttribute('reqspecdisp');
                });
                if (response.data.ps_derivation_ids === undefined) {
                  setResultMessage(`ReqSpec連携しているIDがありませんでした。`);
                } else {
                  const psDerivationIds = response.data.ps_derivation_ids;
                  psDerivationIds.forEach((psDerivationId: string) => {
                    document.querySelectorAll(`[ps_derivation_id='${psDerivationId}']`).forEach((element: Element) => {
                      element.setAttribute('reqspecdisp', 'on');
                    });
                  });
                  setResultMessage(`ReqSpecデータをインポートしました。`);
                }
                setDisplayResult(true);
              },
            }
          );
        }
        reset();
        onClose();
      })();
    },
    [handleSubmit, recAsync, reset, onClose, sectionId]
  );
  // #endregion

  return (
    <>
      <Modal active={active} additionalClassName="mtsp" displayCloseButton onClose={onCancelClicked}>
        <form onSubmit={onSubmit}>
          <fieldset disabled={recIsLoading} className="fieldset-reset">
            <div className="formarea1">
              <label htmlFor="">{t('添付ファイル')}</label>
              <div>
                <FileDrop id="importFiles" name="importFiles" setValue={setValue} />
                {errors.importFiles?.message != null && (
                  <p className="form-error">{errors.importFiles.message.toString()}</p>
                )}
              </div>
            </div>
            <div className="btnfr btnfr2 mb0">
              <div>
                <button type="button" className="text-button" onClick={() => onCancelClicked()}>
                  <span className="btn2 mr10 btnbg-g">
                    <span className="modal-close2">{t('キャンセル')}</span>
                  </span>
                </button>
                <span>
                  <input type="submit" id="" name="" value={t('インポート').toString()} className="btn1" />
                </span>{' '}
              </div>
            </div>
          </fieldset>
        </form>
        {recIsLoading && <SpinnerModal />}
        {displayImportError && (
          <ErrorModal
            onClickOk={() => {
              setDisplayImportError(false);
            }}
            error={recError}
          />
        )}
      </Modal>
      {displayResult && (
        <OkModal
          onClickOk={() => {
            setDisplayResult(false);
            reset();
            onClose();
          }}
        >
          <div>{resultMessage}</div>
        </OkModal>
      )}
    </>
  );
}

export default ReqSpecImportModal;
